import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, Eye, Plus, Pencil, Trash2 } from 'lucide-react'
import { lcRolesApi } from '@/api/roles'
import { LC_PERMISSION_MENU_GROUPS, type LcAdminRole, type LcAdminRoleRequest } from '@/types/rbac'

type RoleFormData = { name: string; description: string; permissions: string[] }
type ModalMode = 'create' | 'edit' | 'view'
const emptyForm: RoleFormData = { name: '', description: '', permissions: [] }

export default function RoleManagement() {
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<ModalMode>('create')
  const [editingRole, setEditingRole] = useState<LcAdminRole | null>(null)
  const [form, setForm] = useState<RoleFormData>(emptyForm)

  const { data } = useQuery({
    queryKey: ['lc-roles'],
    queryFn: async () => (await lcRolesApi.getRoles()).data.data,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['lc-roles'] })

  const closeModal = () => { setIsModalOpen(false); setEditingRole(null); setForm(emptyForm) }

  const createMutation = useMutation({
    mutationFn: (d: LcAdminRoleRequest) => lcRolesApi.createRole(d),
    onSuccess: () => { invalidate(); closeModal() },
    onError: () => toast.error('역할 생성에 실패했습니다.'),
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, d }: { id: number; d: LcAdminRoleRequest }) => lcRolesApi.updateRole(id, d),
    onSuccess: () => { invalidate(); closeModal() },
    onError: () => toast.error('역할 수정에 실패했습니다.'),
  })
  const deleteMutation = useMutation({
    mutationFn: (id: number) => lcRolesApi.deleteRole(id),
    onSuccess: invalidate,
    onError: () => toast.error('역할 삭제 실패. 배정된 직원이 있는지 확인하세요.'),
  })

  const openCreate = () => { setModalMode('create'); setEditingRole(null); setForm(emptyForm); setIsModalOpen(true) }
  const openRoleModal = (r: LcAdminRole, mode: Extract<ModalMode, 'edit' | 'view'>) => {
    setModalMode(mode)
    setEditingRole(r)
    setForm({ name: r.name, description: r.description ?? '', permissions: [...r.permissions] })
    setIsModalOpen(true)
  }
  const openEdit = (r: LcAdminRole) => openRoleModal(r, 'edit')
  const openView = (r: LcAdminRole) => openRoleModal(r, 'view')

  const isGroupSelected = (permissions: string[], groupPermissions: string[]) =>
    groupPermissions.every((permission) => permissions.includes(permission))

  const togglePermissionGroup = (groupPermissions: string[]) =>
    setForm((f) => ({
      ...f,
      permissions: isGroupSelected(f.permissions, groupPermissions)
        ? f.permissions.filter((p) => !groupPermissions.includes(p))
        : Array.from(new Set([...f.permissions, ...groupPermissions])),
    }))

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error('역할 이름을 입력하세요.'); return }
    if (form.permissions.length === 0) { toast.error('최소 1개 이상의 권한을 선택하세요.'); return }
    const d: LcAdminRoleRequest = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      permissions: form.permissions,
    }
    if (editingRole) updateMutation.mutate({ id: editingRole.id, d })
    else createMutation.mutate(d)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold">역할 관리</h1>
        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 flex items-center gap-1"
        >
          <Plus className="size-4" /> 역할 추가
        </button>
      </div>

      {/* 센터 역할 */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 mb-2">센터 역할</h2>
        <div className="space-y-2">
          {data?.roles?.map((r) => (
            <div key={r.id} className="border rounded-lg p-3 flex justify-between items-center">
              <div>
                <p className="font-medium">
                  {r.name} <span className="text-xs text-gray-400">({r.userCount}명)</span>
                  {r.isDefault && (
                    <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      기본 역할
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500">{r.permissions.length}개 권한</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => openView(r)}
                  aria-label={`${r.name} 보기`}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <Eye className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => openEdit(r)}
                  disabled={r.isDefault}
                  aria-label={`${r.name} 수정`}
                  className="text-gray-600 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-300"
                >
                  <Pencil className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => { if (confirm('삭제하시겠습니까?')) deleteMutation.mutate(r.id) }}
                  disabled={r.isDefault}
                  aria-label={`${r.name} 삭제`}
                  className="text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:text-gray-300"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
          ))}
          {!data?.roles?.length && (
            <p className="text-sm text-gray-400">아직 생성된 역할이 없습니다.</p>
          )}
        </div>
      </section>

      {/* 모달 */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={closeModal}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-modal-title"
            className="bg-white rounded-xl p-6 w-[560px] max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="role-modal-title" className="text-lg font-bold mb-4">
              {modalMode === 'view' ? '역할 상세' : editingRole ? '역할 수정' : '역할 추가'}
            </h3>
            <label className="block text-sm mb-1">이름</label>
            <input
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm w-full focus:outline-none focus:border-primary-500 mb-3"
              value={form.name}
              disabled={modalMode === 'view'}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <label className="block text-sm mb-1">설명</label>
            <input
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm w-full focus:outline-none focus:border-primary-500 mb-3"
              value={form.description}
              disabled={modalMode === 'view'}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
            <label className="block text-sm mb-1">접근 가능 페이지</label>
            <div className="grid grid-cols-2 gap-2">
              {LC_PERMISSION_MENU_GROUPS.map((p) => {
                const selected = isGroupSelected(form.permissions, p.permissions)
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => { if (modalMode !== 'view') togglePermissionGroup(p.permissions) }}
                    disabled={modalMode === 'view'}
                    className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm text-left ${
                      selected ? 'border-primary-400 bg-primary-50' : 'border-gray-200'
                    } disabled:cursor-default`}
                  >
                    <span
                      className={`size-4 rounded border flex items-center justify-center ${
                        selected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                      }`}
                    >
                      {selected && <Check className="size-3 text-white" />}
                    </span>
                    {p.label}
                  </button>
                )
              })}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
              >
                {modalMode === 'view' ? '닫기' : '취소'}
              </button>
              {modalMode !== 'view' && (
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                >
                  {editingRole ? '저장' : '생성'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
